import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  STOCK_QTY,
  fmtRsPerShare,
  qualifiesHoldTabSell,
  HOLD_SOFT_AFTER_MIN,
  HOLD_STOP_AFTER_MIN
} from './trading-rules';
import { formatIstDateTimeFromParts, formatCardOpenedAt, formatCardSoldAt } from './ist-datetime';
import TradingCardWhen from './TradingCardWhen';
import MiniCandleChart from './MiniCandleChart';
import MobileSegmentTabs, { useActionSegment } from './MobileSegmentTabs';
import { cardBrokerCharges } from './equity-delivery-charges';
import './TradingActionBoard.css';

function shareBadgeLabel(item) {
  if (item.shareTag === 'new') return 'NEW SHARE';
  if (item.shareTag === 'old') return 'OLD SHARE';
  if (item.shareTag === 'sold-today') return 'SOLD TODAY';
  if (item.shareTag === 'stop-loss') return 'STOP LOSS';
  if (item.shareTag === 'done') return 'DONE';
  if (item.shareTag === 'trailing') return 'TRAILING';
  if (item.shareTag === 'short' || item.positionSide === 'short' || item.side === 'short') return 'SHORT';
  if (item.positionSide === 'long' || item.side === 'long') return 'LONG';
  return item.shareTag === 'old' ? 'OLD SHARE' : 'NEW SHARE';
}

function fromTabBadgeText(item) {
  if (item?.fromTabLabel) return String(item.fromTabLabel);
  const t = String(item?.fromTab || item?.exitKind || '').toLowerCase();
  if (t === 'buy') return 'FROM BUY';
  if (t === 'hold') return 'FROM HOLD';
  if (t === 'sell') return 'FROM SELL';
  if (t === 'stop') return 'FROM STOP';
  if (t === 'trailing' || t === 'peak') return 'TRAILING';
  if (t === 'short' || t === 'cover') return 'SHORT';
  return null;
}

function isShortCard(item) {
  if (!item) return false;
  if (item.shareTag === 'trailing' || item.exitKind === 'trailing' || item.fromTab === 'trailing') return false;
  const side = String(item.side || item.positionSide || '').toLowerCase();
  if (side === 'short' || item.shareTag === 'short') return true;
  if (String(item.type || '').toUpperCase() === 'COVER') return true;
  // Sold card heal: exit below entry but positive booked pnl → SHORT
  const entry = Number(item.buyPrice ?? item.entryPrice ?? item.botPrice);
  const exit = Number(item.sellPrice ?? item.price);
  const pnl = item.pnl != null ? Number(item.pnl) : null;
  if (entry > 0 && exit > 0 && exit < entry - 0.004 && pnl != null && pnl > 0.004) return true;
  const move = item.move != null ? Number(item.move) : null;
  if (move != null && move < -0.004 && pnl != null && pnl > 0.004) return true;
  return false;
}

/** Economic per-share: LONG = exit−entry · SHORT = entry−exit (Rule 3). */
function cardPerShareMove(entry, px, short) {
  if (!(Number(entry) > 0) || !(Number(px) > 0)) return null;
  return short
    ? Math.round((Number(entry) - Number(px)) * 100) / 100
    : Math.round((Number(px) - Number(entry)) * 100) / 100;
}

function inferExchange(symbol) {
  if (!symbol || typeof symbol !== 'string') return null;
  const s = symbol.toUpperCase().trim();
  if (s.includes('.BO') || s.includes('.BSE')) return 'BSE';
  if (s.includes('.NS') || s.includes('.NSE')) return 'NSE';
  if (/^[A-Z]+$/.test(s)) return 'NSE';
  return null;
}

function getExchangeLabel(item) {
  if (!item) return null;
  if (item.exchange && typeof item.exchange === 'string') {
    const exch = item.exchange.trim().toUpperCase();
    if (exch === 'BSE' || exch === 'NSE') return exch;
  }
  return inferExchange(item.symbol) || inferExchange(item.stock);
}

/** Newest last-update first for Sold today / Yesterday lists. */
function cardLastUpdatedMs(item) {
  if (!item) return 0;
  const keys = [
    'lastUpdated',
    'updatedAt',
    'savedAt',
    'soldAt',
    'sellDateTime',
    'boughtAt',
    'openedAt',
    'holdSince'
  ];
  for (const key of keys) {
    const v = item[key];
    if (v == null || v === '') continue;
    const n = typeof v === 'number' ? v : Date.parse(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const date = item.sellDate || item.date || item.positionDate || '';
  const time = item.sellTimeLabel || item.time || '';
  if (date) {
    const parsed = Date.parse(`${date} ${time}`.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function sortByLastUpdatedDesc(rows) {
  return [...(rows || [])].sort((a, b) => cardLastUpdatedMs(b) - cardLastUpdatedMs(a));
}

function ActionCard({
  item,
  tone,
  profitRs = 0.5,
  profitLabel,
  stockQty = STOCK_QTY,
  onSellTabChoice,
  alwaysShowActions = false,
  holdEscapeMin = 0.1,
  holdEscapeMax = 100,
  candlesApi = '/api/normal/candles'
}) {
  const isHold = tone === 'hold';
  const escapeMin = Number(holdEscapeMin) || 0.1;
  const escapeMax = Number(holdEscapeMax) || 100;
  const holdProfitLabel = `₹${escapeMin.toFixed(2)}–₹${escapeMax.toFixed(0)}`;
  const cardProfitRs = Number(item.targetProfitRs ?? item.profitTargetRs) > 0
    ? Number(item.targetProfitRs ?? item.profitTargetRs)
    : Number(profitRs) || 0.5;
  const profitSh = isHold
    ? holdProfitLabel
    : (Number(item.targetProfitRs ?? item.profitTargetRs) > 0
      ? fmtRsPerShare(cardProfitRs)
      : (profitLabel || fmtRsPerShare(profitRs)));
  const historyShare = item.soldToday === true
    || item.kind === 'sold'
    || item.kind === 'alert'
    || item.kind === 'boughtToday';
  const soldDone = tone === 'sell-done' || item.soldToday === true || item.kind === 'sold';
  const short = isShortCard(item);
  const exchange = getExchangeLabel(item);
  // Sold today: never fall back to sell price as "BUY" (shows nonsense like ₹-93)
  const rawBuy = Number(item.buyPrice ?? item.entryPrice ?? item.ourPrice ?? item.investedPrice ?? item.botPrice);
  const rawSell = Number(item.sellPrice ?? item.price);
  const botPrice = soldDone
    ? (rawBuy > 0 ? rawBuy : null)
    : (item.botPrice ?? item.price);
  const ourPrice = soldDone
    ? (rawBuy > 0 ? rawBuy : null)
    : (item.ourPrice ?? item.investedPrice ?? null);
  const live = soldDone ? null : (historyShare ? null : item.currentPrice);
  const timeWindowBadge = !soldDone && (item.holdLabel
    ? String(item.holdLabel).replace(/\s*DONE$/i, '').trim()
    : (item.buyTimeLabel || null));
  const showStageTimer = !soldDone && !historyShare
    && (tone === 'buy' || tone === 'hold' || tone === 'sell')
    && item.countdown
    && item.countdown !== '--:--';
  const timerSub = tone === 'sell'
    ? (item.sellTimerLabel || item.holdRemainingLabel || (short ? 'COVER → Sold Today' : 'SELL → Sold Today'))
    : (item.holdStageHint || item.holdRemainingLabel || item.holdElapsedLabel || null);
  const timerBadge = tone === 'sell'
    ? (item.holdLabel || (short ? 'COVER TIMER' : 'SELL TIMER'))
    : (item.holdLabel || (tone === 'hold' ? 'HOLD' : (short ? 'SHORT' : 'BUY')));
  const timerUrgent = !!(item.buyTimerUrgent || item.sellTimerUrgent || (tone === 'sell' && item.sellTimerUrgent));
  const sellFloorRs = isHold ? escapeMin : cardProfitRs;
  const targetPrice = item.targetPrice ?? (
    botPrice != null
      ? Math.round((Number(botPrice) + (short ? -1 : 1) * (isHold ? escapeMin : cardProfitRs)) * 100) / 100
      : null
  );
  const refEntry = (ourPrice != null && Number(ourPrice) > 0)
    ? Number(ourPrice)
    : (botPrice != null && Number(botPrice) > 0 ? Number(botPrice) : null);
  const sellPxDone = soldDone && rawSell > 0 ? rawSell : null;
  const moveFromPrices = soldDone && sellPxDone != null && refEntry != null
    ? cardPerShareMove(refEntry, sellPxDone, short)
    : (!historyShare && refEntry != null && live != null
      ? cardPerShareMove(refEntry, live, short)
      : null);
  // Prefer price-derived move (SHORT-aware). Fall back to API move only if prices missing.
  let move = moveFromPrices;
  if (move == null && item.move != null && Number.isFinite(Number(item.move))) {
    move = Number(item.move);
    // API move still LONG-formula while card is SHORT → flip
    if (short && move < -0.004 && item.pnl != null && Number(item.pnl) > 0.004) {
      move = Math.round(Math.abs(move) * 100) / 100;
    }
  }
  const profitHit = !soldDone && !historyShare && move != null && (
    isHold
      ? qualifiesHoldTabSell(move, item.holdSince || item.openedAt, Date.now(), {
        profitRs: cardProfitRs,
        escapeMin: holdEscapeMin,
        escapeMax: holdEscapeMax,
        softAfterMin: item.holdSoftAfterMin ?? HOLD_SOFT_AFTER_MIN,
        stopAfterMin: item.holdStopAfterMin ?? HOLD_STOP_AFTER_MIN
      })
      : (Math.round(move * 100) / 100 >= cardProfitRs - 0.004
        || (move >= escapeMin - 0.004 && move <= escapeMax + 0.004))
  );
  const nearTarget = !soldDone && (item.kind === 'near_target' || item.nearTargetBlink);
  const needMore = !soldDone && move != null && move < sellFloorRs
    ? Math.round((sellFloorRs - move) * 100) / 100
    : null;

  const qty = Math.max(1, Number(item.qty) || Number(stockQty) || STOCK_QTY);
  // Sold today: entry/exit only — never use live LTP (null) or sell price as buy.
  const buyPxForCharges = Number(
    soldDone
      ? (item.buyPrice ?? item.entryPrice ?? refEntry ?? botPrice)
      : (item.ask ?? item.buyBrokerPrice ?? live ?? botPrice ?? refEntry)
  ) || 0;
  const sellPxForCharges = Number(
    soldDone
      ? (item.sellPrice ?? item.price)
      : (item.bid ?? item.sellBrokerPrice ?? live ?? targetPrice ?? botPrice)
  ) || 0;
  const investedTurnover = soldDone && Number(item.invested) > 0 ? Number(item.invested) : null;
  const proceedsTurnover = soldDone && Number(item.proceeds) > 0 ? Number(item.proceeds) : null;
  const charges = (investedTurnover != null && proceedsTurnover != null)
    ? cardBrokerCharges({
      buyPrice: investedTurnover / qty,
      sellPrice: proceedsTurnover / qty,
      qty
    })
    : ((buyPxForCharges > 0 || sellPxForCharges > 0)
      ? cardBrokerCharges({
        buyPrice: buyPxForCharges || sellPxForCharges,
        sellPrice: sellPxForCharges || buyPxForCharges,
        qty
      })
      : null);
  // Live Zerodha schedule only for Sold today (ignore stale API CNC brokerage/net).
  const buyChargeTotal = charges?.buyTotal != null
    ? Number(charges.buyTotal)
    : (!soldDone && item.chargesBuy != null
      ? Number(item.chargesBuy)
      : (!soldDone && item.brokerageBuy != null ? Number(item.brokerageBuy) : null));
  const sellChargeTotal = charges?.sellTotal != null
    ? Number(charges.sellTotal)
    : (!soldDone && item.chargesSell != null
      ? Number(item.chargesSell)
      : (!soldDone && item.brokerageSell != null ? Number(item.brokerageSell) : null));
  const buyPerShare = buyChargeTotal != null
    ? Math.round((Number(buyChargeTotal) / qty) * 100) / 100
    : (charges?.buyPerShare ?? null);
  const sellPerShare = sellChargeTotal != null
    ? Math.round((Number(sellChargeTotal) / qty) * 100) / 100
    : (charges?.sellPerShare ?? null);
  const feeTotal = (buyChargeTotal != null && sellChargeTotal != null)
    ? Math.round((Number(buyChargeTotal) + Number(sellChargeTotal)) * 100) / 100
    : (charges?.roundTrip ?? null);
  // Sold: gross from move×qty (Rule 3 path), not API net mistakenly stored as pnl
  const grossTotal = (soldDone && move != null)
    ? Math.round(Number(move) * qty * 100) / 100
    : (item.pnl != null
      ? Math.round(Number(item.pnl) * 100) / 100
      : (move != null ? Math.round(Number(move) * qty * 100) / 100 : null));
  // Net = gross − live fees only (never fall back to stale API netPnl)
  const netTotal = (grossTotal != null && feeTotal != null)
    ? Math.round((grossTotal - feeTotal) * 100) / 100
    : null;
  const netPerShare = netTotal != null
    ? Math.round((netTotal / qty) * 100) / 100
    : null;
  const netState = netTotal == null && netPerShare == null
    ? null
    : ((netTotal ?? netPerShare) > 0.004 ? 'up' : ((netTotal ?? netPerShare) < -0.004 ? 'down' : 'flat'));
  const pnlState = move == null
    ? null
    : (move > 0.004 ? 'profit' : move < -0.004 ? 'loss' : 'flat');
  const pnlLabel = pnlState === 'profit'
    ? (soldDone ? 'Profit' : 'In profit')
    : pnlState === 'loss'
      ? (soldDone ? 'Loss' : 'In loss')
      : (pnlState === 'flat' ? 'Flat' : null);
  const pnlBadgeValue = move != null
    ? `${move >= 0 ? '+' : ''}₹${Number(move).toFixed(2)}/sh`
    : null;

  const isLiveSellAlert = (tone === 'sell' || tone === 'sell-done' || tone === 'strategy' || tone === 'yesterday')
    && item.liveSellAlert && !item.soldToday;
  const alertKind = isLiveSellAlert
    ? (item.kind === 'near_target' ? 'near' : item.kind === 'urgent' || profitHit ? 'urgent' : 'rotate')
    : null;
  const boughtWhen = formatCardOpenedAt(item);
  const soldWhen = formatCardSoldAt(item);
  const displayQty = item.qty != null ? item.qty : stockQty;

  return (
    <div
      className={[
        'tab-card',
        tone,
        item.pending ? 'pending' : '',
        item.stopHold ? 'stop-hold' : '',
        isLiveSellAlert ? `sell-live-alert alert-${alertKind}` : '',
        isLiveSellAlert && profitHit ? 'alert-profit-blink' : '',
        isLiveSellAlert && nearTarget ? 'alert-near-glow' : '',
        !isLiveSellAlert && profitHit ? 'profit-hit target-blink' : '',
        !isLiveSellAlert && nearTarget ? 'near-target-blink window-glow' : ''
      ].filter(Boolean).join(' ')}
    >
      {isLiveSellAlert && (
        <span className="tab-sell-alert-badge">LIVE ALERT</span>
      )}
      <div className="tab-card-top">
        <div className="tab-card-title-group">
          <strong>{item.stock}</strong>
          {exchange && (
            <span className="tab-exchange-badge">{exchange}</span>
          )}
          {soldDone && item.shareTag !== 'sold-today' && (
            <span className="tab-share-badge sold-today">SOLD</span>
          )}
          {timeWindowBadge && (
            <span className="tab-time-window-badge">{timeWindowBadge}</span>
          )}
        </div>
        <div className="tab-qty-badge" title={`${displayQty} shares`}>
          <span className="tab-qty-label">Qty</span>
          <strong>×{displayQty}</strong>
        </div>
      </div>
      {(item.shareTag || item.yesterdayLabel || item.strategyLabel || item.fromSellChoice || item.strategyCompareOnly || fromTabBadgeText(item)) && (
        <div className="tab-card-share-meta">
          {fromTabBadgeText(item) && (
            <span className={`tab-share-badge from-tab from-${String(item.fromTab || item.exitKind || 'sell').toLowerCase()}`}>
              {fromTabBadgeText(item)}
            </span>
          )}
          {item.shareTag && (
            <span className={`tab-share-badge ${item.shareTag}`}>
              {shareBadgeLabel(item)}
            </span>
          )}
          {item.yesterdayLabel && (
            <span className="tab-share-badge yesterday">{item.yesterdayLabel}</span>
          )}
          {item.strategyLabel && (
            <span className="tab-share-badge strategy-tag">{item.strategyLabel}</span>
          )}
          {item.fromSellChoice && tone === 'buy' && (
            <span className="tab-share-badge rebuy">REBUY</span>
          )}
          {item.strategyCompareOnly && (
            <span className="tab-share-badge strategy-test">STRATEGY TEST</span>
          )}
        </div>
      )}
      {showStageTimer && (
        <div
          className={[
            'tab-card-hold-timer',
            tone === 'hold' ? 'hold-tab' : '',
            tone === 'sell' ? 'sell-tab-timer' : '',
            item.holdStage === 'late' ? 'late-stage' : '',
            timerUrgent ? 'urgent' : ''
          ].filter(Boolean).join(' ')}
        >
          <span className="tab-hold-timer-badge">{timerBadge}</span>
          <strong className="tab-hold-countdown">{item.countdown}</strong>
          {timerSub && (
            <span className="tab-hold-timer-sub">{timerSub}</span>
          )}
        </div>
      )}
      {tone === 'hold' && !soldDone && (item.softCountdown || item.stopCountdown) && (
        <div className="tab-hold-stage-row" title="HOLD multi-stage timers from Master">
          <span>Soft {item.softCountdown || '—'}</span>
          <span>Late {item.stopCountdown || '—'}</span>
        </div>
      )}
      <hr className="tab-card-divider" aria-hidden="true" />
      <TradingCardWhen
        item={item}
        prefix={soldWhen
          ? (short ? 'Covered' : 'Sold')
          : (boughtWhen ? (short ? 'Shorted' : 'Bought') : (tone === 'buy' ? (short ? 'Watching SHORT' : 'Watching') : 'Updated'))}
      />
      <div className="tab-mini-summary">
        {pnlLabel && pnlBadgeValue && (
          <div className={`tab-pnl-badge ${pnlState}`}>
            <span className="tab-pnl-status">{pnlLabel}</span>
            <strong>{pnlBadgeValue}</strong>
          </div>
        )}

        <div className="tab-mini-prices">
          <div className="tab-mini-cell">
            <span>{short ? 'Short @' : 'Buy'}</span>
            <strong>{refEntry != null ? `₹${Number(refEntry).toFixed(2)}` : '—'}</strong>
          </div>
          <div className="tab-mini-cell">
            <span>Live</span>
            <strong className={`${item.priceDir || ''} ${move != null && move < 0 ? 'down' : ''} ${move != null && move >= 0 ? 'up' : ''} ${profitHit ? 'target-blink-price' : ''}`}>
              {soldDone
                ? (sellPxDone != null ? `₹${Number(sellPxDone).toFixed(2)}` : '—')
                : (live != null ? `₹${Number(live).toFixed(2)}` : (botPrice != null ? `₹${Number(botPrice).toFixed(2)}` : '—'))}
            </strong>
          </div>
          <div className="tab-mini-cell target">
            <span>{short
              ? (isHold ? `Cover @ ${profitSh}` : `Cover ↓ ${profitSh}`)
              : `Sell @ +${profitSh}`}</span>
            <strong>{soldDone
              ? (sellPxDone != null ? `₹${Number(sellPxDone).toFixed(2)}` : '—')
              : (targetPrice != null ? `₹${targetPrice.toFixed(2)}` : '—')}</strong>
          </div>
        </div>

        {(buyChargeTotal != null || sellChargeTotal != null) && (
          <div className="tab-fee-card" title={charges ? `${charges.buyTip}\n${charges.sellTip}` : 'CNC broker charges estimate'}>
            <p className="tab-fee-title">Broker charges</p>
            <div className="tab-fee-grid tab-fee-grid-5">
              <div className="tab-fee-cell buy">
                <span>Buy fee</span>
                <strong>{buyChargeTotal != null ? `₹${Number(buyChargeTotal).toFixed(0)}` : '—'}</strong>
              </div>
              <div className="tab-fee-cell buy-ps">
                <span>Buy/sh</span>
                <strong>{buyPerShare != null ? `₹${Number(buyPerShare).toFixed(2)}` : '—'}</strong>
              </div>
              <div className="tab-fee-cell sell">
                <span>Sell fee</span>
                <strong>{sellChargeTotal != null ? `₹${Number(sellChargeTotal).toFixed(0)}` : '—'}</strong>
              </div>
              <div className="tab-fee-cell sell-ps">
                <span>Sell/sh</span>
                <strong>{sellPerShare != null ? `₹${Number(sellPerShare).toFixed(2)}` : '—'}</strong>
              </div>
              <div className="tab-fee-cell total">
                <span>Total</span>
                <strong>
                  {feeTotal != null
                    ? `₹${Number(feeTotal).toFixed(0)}`
                    : '—'}
                </strong>
              </div>
            </div>
          </div>
        )}

        {(netPerShare != null || netTotal != null) && (
          <div
            className={`tab-net-card ${netState || ''}`}
            title="Net = gross P&L − buy/sell Zerodha charges (Intraday/MIS or CNC from Master)"
          >
            <p className="tab-net-title">Net profit</p>
            <div className="tab-net-grid">
              <div className={`tab-net-cell ${netState || ''}`}>
                <span>Net /sh</span>
                <strong>
                  {netPerShare != null
                    ? `${netPerShare >= 0 ? '+' : ''}₹${Number(netPerShare).toFixed(2)}`
                    : '—'}
                </strong>
              </div>
              <div
                className={`tab-net-cell overall ${netState || ''}`}
                title={`Net ×${qty} after charges`}
              >
                <span>Net ×{qty}</span>
                <strong>
                  {netTotal != null
                    ? `${netTotal >= 0 ? '+' : ''}₹${Number(netTotal).toFixed(2)}`
                    : '—'}
                </strong>
              </div>
            </div>
          </div>
        )}
      </div>
      {(tone === 'buy' || tone === 'hold' || tone === 'sell' || soldDone || tone === 'sell-done') && (
        <MiniCandleChart
          item={item}
          livePrice={soldDone ? (item.sellPrice ?? live) : live}
          entryPrice={refEntry}
          exitPrice={soldDone ? (item.sellPrice ?? null) : null}
          candlesApi={candlesApi}
        />
      )}
      {move != null && (
        <div className={`tab-card-move ${soldDone ? 'done' : profitHit ? 'hit' : nearTarget ? 'near' : move >= 0 ? 'up' : 'down'}`}>
          {soldDone
            ? (move >= 0 ? '+' : '') + `₹${move.toFixed(2)}/sh · closed on Groww`
            : (
              <>
                {move >= 0 ? '+' : ''}₹{move.toFixed(2)}/sh
                {profitHit
                  ? ' · SELL NOW'
                  : nearTarget
                    ? ' · near target — SELL'
                    : needMore != null && needMore > 0
                      ? (isHold
                        ? ` · need +₹${needMore.toFixed(2)} for HOLD sell`
                        : ` · need +₹${needMore.toFixed(2)} for sell`)
                      : historyShare && item.kind === 'boughtToday'
                        ? ' · bot bought earlier today'
                        : ''}
              </>
            )}
        </div>
      )}
      <p className="tab-card-reason">{item.reason}</p>
      {item.winRate != null && tone === 'buy' && (
        <span className="tab-card-win">~{item.winRate}% win rate</span>
      )}
      {item.soldToday || item.kind === 'boughtToday' ? (
        <span className="tab-card-time sell-done">
          {item.kind === 'boughtToday' ? 'Bought' : item.kind === 'sold' ? 'Sold' : 'Alert'} at{' '}
          {soldWhen || boughtWhen
            || formatIstDateTimeFromParts(item.sellDate, item.sellTimeLabel || item.timeLeft || item.time)
            || item.timeLeft
            || item.time
            || '—'}
        </span>
      ) : item.timeLeft && tone !== 'buy' && tone !== 'hold' ? (
        <span className={`tab-card-time${tone === 'sell' ? ' sell-timer' : ''}`}>{item.timeLeft}</span>
      ) : null}
      {item.slot != null && <span className="tab-card-slot">Box {item.slot}</span>}
      {(alwaysShowActions || tone === 'sell' || tone === 'sell-done') && onSellTabChoice && !historyShare && (
        <div className="tab-sell-actions" role="group" aria-label={`Choose action for ${item.stock}`}>
          <button type="button" className="tab-sell-btn sold" onClick={() => onSellTabChoice(item, 'sold')}>
            SOLD
          </button>
          <button type="button" className="tab-sell-btn hold" onClick={() => onSellTabChoice(item, 'hold')}>
            HOLD
          </button>
          <button type="button" className="tab-sell-btn buy" onClick={() => onSellTabChoice(item, 'buy')}>
            BUY
          </button>
        </div>
      )}
    </div>
  );
}

function PerformerBox({ item }) {
  const pct = item.changePct != null ? Number(item.changePct) : null;
  const price = item.price != null ? Number(item.price) : null;
  const pctLabel = pct != null && Number.isFinite(pct)
    ? `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`
    : '—';
  const exchange = getExchangeLabel(item);
  return (
    <article className="performer-box" aria-label={`${item.stock} rank ${item.rank}`}>
      <span className="performer-rank">#{item.rank ?? '—'}</span>
      <div className="performer-main">
        <strong className="performer-stock">{item.stock}</strong>
        {exchange && <span className="performer-exchange">{exchange}</span>}
      </div>
      <div className="performer-stats">
        {price != null && Number.isFinite(price) && (
          <span className="performer-price">₹{price.toFixed(2)}</span>
        )}
        <span className={`performer-change${pct != null && pct >= 0 ? ' up' : pct != null ? ' down' : ''}`}>
          {pctLabel}
        </span>
      </div>
      {item.einsteinScore != null && (
        <span className="performer-einstein">Einstein {Math.round(item.einsteinScore)}%</span>
      )}
    </article>
  );
}

export default function TradingActionBoard({
  buy = [],
  hold = [],
  sell = [],
  sellActive = [],
  sellHistory = [],
  yesterday = [],
  bestPerformersToday = null,
  stopLossToday = [],
  shortToday = [],
  sellUrgentCount = 0,
  sellTodayCount = 0,
  activeStep = 1,
  profitLabel = '₹0.50',
  profitRs = 0.5,
  holdPartialMin = 0.3,
  holdPartialMax = 0.5,
  holdMinutes = 10,
  stockQty = STOCK_QTY,
  slotsMax = 5,
  quantActionOnly = false,
  volatileMode = true,
  quantSlotCount = 0,
  liveStatus = 'idle',
  priceSource = null,
  onCopyAllBuy,
  onRefreshBuys,
  refreshBuyBusy = false,
  onSellConfirmAll,
  sellConfirmBusy = false,
  fullyAutomated = true,
  onSellTabChoice,
  onYesterdayTabChoice,
  strategyCompareMode = false,
  slotsFree = null,
  holdSlotCount = 0,
  buyPendingCount = 0,
  onRefreshSoldToday = null,
  soldTodayRefreshing = false,
  candlesApi = '/api/normal/candles',
  /** Hide Yesterday / Stop loss / Trailing (Best Performer keeps Buy/Hold/Sell + Sold today only). */
  hideSecondaryTabs = false
}) {
  const partialMinLabel = fmtRsPerShare(holdPartialMin);
  const partialMaxLabel = fmtRsPerShare(holdPartialMax);
  const partialRangeLabel = `${partialMinLabel}–${partialMaxLabel}`;
  const stepLabel = activeStep === 2 ? 'SELL NOW on Groww' : activeStep === 1 ? 'BUY on Groww' : 'HOLD — watch alerts';
  const liveLabel = liveStatus === 'live'
    ? (priceSource === 'yahoo_live' ? 'Yahoo live LTP' : priceSource === 'yahoo_miss' ? 'Yahoo unavailable' : 'Yahoo Finance')
    : liveStatus === 'reconnecting' ? 'Reconnecting…' : '';

  const activeSell = sellActive.length ? sellActive : sell.filter((item) => !item.soldToday && item.kind !== 'sold' && item.kind !== 'alert');
  const historySell = useMemo(() => {
    const raw = sellHistory.length
      ? sellHistory
      : sell.filter((item) => item.soldToday || item.kind === 'sold' || item.kind === 'alert');
    return sortByLastUpdatedDesc(raw);
  }, [sellHistory, sell]);
  const yesterdaySorted = useMemo(() => sortByLastUpdatedDesc(yesterday), [yesterday]);
  const performersList = useMemo(() => {
    if (!Array.isArray(bestPerformersToday)) return [];
    return [...bestPerformersToday].sort((a, b) => (Number(a?.rank) || 999) - (Number(b?.rank) || 999));
  }, [bestPerformersToday]);
  const showPerformersTab = !hideSecondaryTabs && bestPerformersToday != null;
  const stopLossSorted = useMemo(() => sortByLastUpdatedDesc(stopLossToday), [stopLossToday]);
  const shortTodaySorted = useMemo(() => sortByLastUpdatedDesc(shortToday), [shortToday]);

  const [mobileTab, setMobileTab] = useActionSegment(activeStep, sellUrgentCount > 0 || activeSell.length > 0);
  const [mobileLowerTab, setMobileLowerTab] = useState('sold');
  const mobileTabs = [
    { id: 'buy', label: 'Buy', count: buy.length },
    { id: 'hold', label: 'Hold', count: hold.length },
    { id: 'sell', label: 'Sell', count: activeSell.length, urgent: sellUrgentCount > 0 }
  ];
  const mobileLowerTabs = hideSecondaryTabs
    ? [{ id: 'sold', label: 'Sold today', count: historySell.length }]
    : showPerformersTab
      ? [
          { id: 'sold', label: 'Sold today', count: historySell.length },
          { id: 'performers', label: 'Best performers today', count: performersList.length },
          { id: 'stoploss', label: 'Stop loss', count: stopLossSorted.length },
          { id: 'short', label: 'Trailing today', count: shortTodaySorted.length }
        ]
      : [
          { id: 'sold', label: 'Sold today', count: historySell.length },
          { id: 'yesterday', label: 'Yesterday', count: yesterdaySorted.length },
          { id: 'stoploss', label: 'Stop loss', count: stopLossSorted.length },
          { id: 'short', label: 'Trailing today', count: shortTodaySorted.length }
        ];

  useEffect(() => {
    if (!onRefreshSoldToday || mobileLowerTab !== 'sold') return undefined;
    onRefreshSoldToday();
  }, [mobileLowerTab, onRefreshSoldToday]);

  return (
    <section className="tab-board" aria-label="What to buy hold sell">
      <header className="tab-board-head">
        <div>
          <h2>Your action screen</h2>
          <p>
            {volatileMode
            ? `AI volatile picks — ${quantSlotCount} stocks · +${profitLabel}/sh · ${fullyAutomated ? 'bot auto-sells (Rules 1–8)' : 'mirror on Groww then confirm'}`
              : quantActionOnly
              ? `Quant Δ picks only — ${quantSlotCount} profitable-edge stocks (breakout · beats Nifty · BUY signal) → mirror on Groww → +${profitLabel}/sh`
              : `Bot picks → mirror on Groww → sell only on profit (+${profitLabel}/sh)`}
            {liveLabel && <em className={`tab-live-badge ${liveStatus}`}> · {liveLabel}</em>}
          </p>
        </div>
        <div className={`tab-now-pill step-${activeStep}`}>
          <span>Do this now</span>
          <strong>{stepLabel}</strong>
        </div>
      </header>

      <MobileSegmentTabs tabs={mobileTabs} value={mobileTab} onChange={setMobileTab} />

      <div className={`tab-columns tab-columns-three tab-board-primary mobile-tab-${mobileTab}`}>
        <div className={`tab-col buy ${activeStep === 1 ? 'active-col' : ''}${mobileTab === 'buy' ? ' mobile-show' : ''}`}>
          <div className="tab-col-head">
            <span className="tab-icon">🟢</span>
            <div>
              <strong>BUY</strong>
              <small>{holdMinutes} min · +{profitLabel}/sh or +{partialMinLabel}+ → SELL · weak → drop (no HOLD dump)</small>
            </div>
            <em className="tab-count">{buy.length}</em>
          </div>
          <div className="tab-col-body">
            {buy.length === 0 ? (
              <p className="tab-empty">
                {slotsFree === 0 && holdSlotCount > 0
                  ? `HOLD has ${holdSlotCount} stock(s) — AI pipeline adds 1 BUY / 1 min`
                  : volatileMode
                    ? `AI pipeline running — SCAN → PREDICT → VALIDATE → 1 stock / 1 min into BUY…`
                    : quantActionOnly
                      ? 'No quant Δ edge right now — bot scanning…'
                      : 'All bot stocks bought on Groww ✓'}
              </p>
            ) : (
              buy.map((item) => (
                <ActionCard key={`buy-${item.stock}-${item.slot}-${item.kind || 'live'}`} item={item} tone={item.kind === 'boughtToday' ? 'sell-done' : 'buy'} profitRs={profitRs} profitLabel={profitLabel} stockQty={stockQty} candlesApi={candlesApi} />
              ))
            )}
          </div>
          {onRefreshBuys && (
            <button
              type="button"
              className="tab-col-btn refresh"
              disabled={refreshBuyBusy}
              onClick={onRefreshBuys}
            >
              {refreshBuyBusy ? 'Refreshing…' : 'Clear all → 5 new buys'}
            </button>
          )}
          {buy.length > 0 && onCopyAllBuy && (
            <button type="button" className="tab-col-btn buy" onClick={onCopyAllBuy}>
              Copy all {buy.length} buy orders
            </button>
          )}
          <p className="tab-col-foot">
            {strategyCompareMode
              ? 'Test compare only — switch to Live tab for ₹10,000 / 5 real buys'
              : fullyAutomated
                ? `${slotsMax} slots · qty from capital · ${holdMinutes} min BUY · Einstein 100%`
                : `${slotsMax} slots · qty from capital · Einstein 100% · Groww mirror`}
          </p>
        </div>

        <div className={`tab-col hold ${activeStep === 3 ? 'active-col' : ''}${mobileTab === 'hold' ? ' mobile-show' : ''}`}>
          <div className="tab-col-head">
            <span className="tab-icon">🟡</span>
            <div>
              <strong>HOLD</strong>
              <small>Keep in Groww — timer shows hold time</small>
            </div>
            <em className="tab-count">{hold.length}</em>
          </div>
          <div className="tab-col-body">
            {hold.length === 0 ? (
              <p className="tab-empty">No open holds — bot positions show here after buy</p>
            ) : (
              hold.map((item) => (
                <ActionCard
                  key={`hold-${item.stock}-${item.slot}`}
                  item={item}
                  tone="hold"
                  profitRs={profitRs}
                  profitLabel={profitLabel}
                  stockQty={stockQty}
                  holdEscapeMin={holdPartialMin}
                  holdEscapeMax={holdPartialMax}
                  candlesApi={candlesApi}
                />
              ))
            )}
          </div>
          <p className="tab-col-foot">
            HOLD stages: full {profitLabel}/sh anytime → SELL · soft band → SELL · late any profit → SELL · net −1% invested → Stop loss → Sold Today
          </p>
        </div>

        <div className={`tab-col sell ${activeStep === 2 ? 'active-col' : ''}${mobileTab === 'sell' ? ' mobile-show' : ''}`}>
          <div className="tab-col-head">
            <span className="tab-icon">🔴</span>
            <div>
              <strong>SELL</strong>
              <small>
                +{profitLabel}/share profit · {sellUrgentCount > 0 ? `${sellUrgentCount} live alert` : 'no live alert'}
                {sellTodayCount > 0 ? ` · ${sellTodayCount} today` : ''}
              </small>
            </div>
            <em className="tab-count">{activeSell.length}</em>
          </div>
          <div className="tab-col-body">
            {activeSell.length === 0 ? (
              <p className="tab-empty">
                {historySell.length > 0
                  ? 'No live sell alerts — see Sold today below'
                  : `No sells today — hold for +${profitLabel}/sh alert`}
              </p>
            ) : (
              activeSell.map((item) => (
                <ActionCard
                  key={`sell-active-${item.kind || 'item'}-${item.stock}-${item.timeLeft}-${item.slot ?? 'x'}`}
                  item={item}
                  tone="sell"
                  profitRs={profitRs}
                  profitLabel={profitLabel}
                  onSellTabChoice={onSellTabChoice}
                  candlesApi={candlesApi}
                />
              ))
            )}
          </div>
          {sellUrgentCount > 0 && onSellConfirmAll && !fullyAutomated && (
            <button
              type="button"
              className="tab-col-btn sell"
              disabled={sellConfirmBusy}
              onClick={onSellConfirmAll}
            >
              {sellConfirmBusy ? 'Confirming…' : `Sell Confirm — ${sellUrgentCount} stock(s)`}
            </button>
          )}
          <p className="tab-col-foot">
            {fullyAutomated
              ? `Timer on each card · beep + voice when share enters SELL · after 30s → Sold Today · REBUY if still rising`
              : `Beep + voice on SELL · countdown → Sold Today · sell on Groww`}
          </p>
        </div>
      </div>

      <MobileSegmentTabs
        className="mobile-seg-lower"
        tabs={mobileLowerTabs}
        value={mobileLowerTab}
        onChange={setMobileLowerTab}
      />

      <div className={`tab-columns ${hideSecondaryTabs ? 'tab-columns-one' : 'tab-columns-four'} tab-board-secondary mobile-tab-${mobileLowerTab}`}>
        <div className={`tab-col sold${mobileLowerTab === 'sold' ? ' mobile-show' : ''}`}>
          <div className="tab-col-head">
            <span className="tab-icon">✅</span>
            <div>
              <strong>Sold today</strong>
              <small>
                Newest first · auto refresh 30s
                {soldTodayRefreshing ? ' · refreshing…' : ''}
              </small>
            </div>
            <em className="tab-count">{historySell.length}</em>
          </div>
          <div className="tab-col-body">
            {historySell.length === 0 ? (
              <p className="tab-empty">
                No sells yet today — once sold, they stay here.
                {' '}
                <Link to={hideSecondaryTabs ? '/Report?screen=best-performer' : '/NormalTrading/Sold'}>
                  {hideSecondaryTabs ? 'Report →' : 'Full history →'}
                </Link>
              </p>
            ) : (
              historySell.map((item) => (
                <ActionCard
                  key={`sold-today-${item.kind || 'item'}-${item.stock}-${item.savedAt || item.lastUpdated || item.sellDateTime || item.timeLeft}-${item.slot ?? 'x'}`}
                  item={item}
                  tone="sell-done"
                  profitRs={profitRs}
                  profitLabel={profitLabel}
                  stockQty={stockQty}
                  candlesApi={candlesApi}
                />
              ))
            )}
          </div>
          <p className="tab-col-foot">
            Permanent history — never deleted · <Link to={hideSecondaryTabs ? '/Report?screen=best-performer' : '/NormalTrading/Sold'}>
              {hideSecondaryTabs ? 'Best Performer report →' : 'all-time sold →'}
            </Link>
          </p>
        </div>

        {!hideSecondaryTabs && (showPerformersTab ? (
          <div className={`tab-col performers${mobileLowerTab === 'performers' ? ' mobile-show' : ''}`}>
            <div className="tab-col-head">
              <span className="tab-icon">🏆</span>
              <div>
                <strong>Best performers today</strong>
                <small>Top 50 day gainers · Master price band · live scan</small>
              </div>
              <em className="tab-count">{performersList.length}</em>
            </div>
            <div className="tab-col-body performers-grid-wrap">
              {performersList.length === 0 ? (
                <p className="tab-empty">Scan warming up — top gainers appear when live quotes load</p>
              ) : (
                <div className="performers-grid">
                  {performersList.map((item) => (
                    <PerformerBox
                      key={`perf-${item.rank}-${item.stock}-${item.symbol || 'x'}`}
                      item={item}
                    />
                  ))}
                </div>
              )}
            </div>
            <p className="tab-col-foot">Ranked by today&apos;s % change · refreshes with live board</p>
          </div>
        ) : (
          <div className={`tab-col yesterday${mobileLowerTab === 'yesterday' ? ' mobile-show' : ''}`}>
            <div className="tab-col-head">
              <span className="tab-icon">📅</span>
              <div>
                <strong>Yesterday stocks</strong>
                <small>Newest update first · carried over · SOLD / HOLD / BUY</small>
              </div>
              <em className="tab-count">{yesterdaySorted.length}</em>
            </div>
            <div className="tab-col-body">
              {yesterdaySorted.length === 0 ? (
                <p className="tab-empty">No yesterday stocks — open holds from prior days appear here when trading starts</p>
              ) : (
                yesterdaySorted.map((item) => (
                  <ActionCard
                    key={`yesterday-${item.stock}-${item.slot ?? 'x'}-${item.openedAt || item.lastUpdated || 'x'}`}
                    item={item}
                    tone="yesterday"
                    profitRs={profitRs}
                    profitLabel={profitLabel}
                    onSellTabChoice={onYesterdayTabChoice}
                    alwaysShowActions
                    candlesApi={candlesApi}
                  />
                ))
              )}
            </div>
            <p className="tab-col-foot">Always choose SOLD / HOLD / BUY for each carried stock</p>
          </div>
        ))}

        {!hideSecondaryTabs && (
        <div className={`tab-col stop-loss${mobileLowerTab === 'stoploss' ? ' mobile-show' : ''}`}>
          <div className="tab-col-head">
            <span className="tab-icon">🛑</span>
            <div>
              <strong>Stop loss today</strong>
              <small>HOLD: net loss −₹0.10…−₹10 OR net profit ≥ ₹2 → Sold Today</small>
            </div>
            <em className="tab-count">{stopLossSorted.length}</em>
          </div>
          <div className="tab-col-body">
            {stopLossSorted.length === 0 ? (
              <p className="tab-empty">No net stop exits today</p>
            ) : (
              stopLossSorted.map((item) => (
                <ActionCard
                  key={`stop-${item.stock}-${item.id || item.savedAt || item.sellDateTime || 'x'}`}
                  item={{ ...item, shareTag: 'stop-loss' }}
                  tone="stop-loss"
                  profitRs={profitRs}
                  profitLabel={profitLabel}
                  stockQty={stockQty}
                  candlesApi={candlesApi}
                />
              ))
            )}
          </div>
          <p className="tab-col-foot">NET×qty hits −₹0.10…−₹10 (or worse) → Sold Today now · capital freed</p>
        </div>
        )}

        {!hideSecondaryTabs && (
        <div className={`tab-col short-today${mobileLowerTab === 'short' ? ' mobile-show' : ''}`}>
          <div className="tab-col-head">
            <span className="tab-icon">📉</span>
            <div>
              <strong>Trailing today</strong>
              <small>Peak pullback trailing → SELL → Sold · lock profit after peak</small>
            </div>
            <em className="tab-count">{shortTodaySorted.length}</em>
          </div>
          <div className="tab-col-body">
            {shortTodaySorted.length === 0 ? (
              <p className="tab-empty">No trailing exits yet — peak +₹arm then drop ₹pullback → Trailing</p>
            ) : (
              shortTodaySorted.map((item) => (
                <ActionCard
                  key={`trail-${item.stock}-${item.id || item.savedAt || item.sellDateTime || item.openedAt || 'x'}`}
                  item={{
                    ...item,
                    shareTag: item.shareTag === 'short' ? 'short' : 'trailing',
                    soldToday: true,
                    kind: 'sold',
                    currentPrice: null,
                    live: false,
                    priceFrozen: true
                  }}
                  tone="sell-done"
                  profitRs={profitRs}
                  profitLabel={profitLabel}
                  stockQty={stockQty}
                  candlesApi={candlesApi}
                />
              ))
            )}
          </div>
          <p className="tab-col-foot">LONG only · trailing locks peak profit · no SHORT selling</p>
        </div>
        )}
      </div>

      <footer className="tab-board-foot">
        <span><strong>1</strong> BUY 5 × 50 sh (unlimited cash)</span>
        <span>→</span>
        <span><strong>2</strong> Weak BUY dropped (no HOLD dump)</span>
        <span>→</span>
        <span><strong>3</strong> SELL at +{profitLabel} or +{partialRangeLabel} now</span>
      </footer>
    </section>
  );
}
